import React from 'react';
import { Box, Paper, Typography, Skeleton, Divider, Stack, Chip, Card } from '@mui/material';
import AssessmentIcon from '@mui/icons-material/Assessment';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CandlestickChartIcon from '@mui/icons-material/CandlestickChart';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../context/AuthContext';
import { useFormatter } from '../../../context/FormatterContext';

interface IncomeDetail {
    label: string;
    amount: number;
    translationKey: string;
}

interface UnifiedTaxSummaryBoxProps {
    totalAmount: number | null;
    details: IncomeDetail[];
    isPaid: boolean;
    isLoading?: boolean;
    selectedYear?: string;
    issueCount?: number;
    savingsPotential?: number;
    costBasisCount?: number;
    missingPriceCount?: number;
    onGoToSmartCheck?: () => void;
}

const UnifiedTaxSummaryBox: React.FC<UnifiedTaxSummaryBoxProps> = ({ 
    totalAmount, 
    details = [], 
    isPaid, 
    isLoading,
    selectedYear,
    issueCount = 0,
    savingsPotential = 0,
    costBasisCount = 0,
    missingPriceCount = 0,
    onGoToSmartCheck
}) => {
    const { t } = useTranslation();
    const { currentCtUser } = useAuth();
    const { formatCurrency } = useFormatter();

    const formatAmount = (value: number | null) => {
        if (value === null || value === undefined) return '---';
        return formatCurrency(value);
    };

    // Default structure if no details provided
    const defaultDetails: IncomeDetail[] = [
        {
            label: 'Private sales income (§ 23 EStG)',
            amount: 0,
            translationKey: 'taxReports.privateSalesIncome'
        },
        {
            label: 'Other income (§ 22 Nr. 3 EStG)',
            amount: 0,
            translationKey: 'taxReports.otherIncome'
        },
        {
            label: 'Capital gains from margin/derivatives',
            amount: 0,
            translationKey: 'taxReports.capitalGains'
        }
    ];

    const displayDetails = details.length > 0 ? details : defaultDetails;

    // Get icon for each category
    const getCategoryIcon = (index: number) => {
        const icons = [
            <TrendingUpIcon sx={{ color: '#4CAF50', fontSize: 20 }} />,
            <AccountBalanceWalletIcon sx={{ color: '#FF9800', fontSize: 20 }} />,
            <CandlestickChartIcon sx={{ color: '#2196F3', fontSize: 20 }} />
        ];
        return icons[index] || icons[0];
    };

    // Scroll to purchase box
    const handleUnlockClick = () => {
        const purchaseElement = document.querySelector('[data-purchase-box]');
        if (purchaseElement) {
            const elementRect = purchaseElement.getBoundingClientRect();
            const absoluteElementTop = elementRect.top + window.pageYOffset;
            const offset = 100; // 100px above the element
            
            window.scrollTo({
                top: absoluteElementTop - offset,
                behavior: 'smooth'
            });
        }
    };

    return (
        <Card
            variant="outlined"
            sx={{}}
        >
            {/* Header with total */}
            <Box sx={{ mb: 1 }}>
                {/* Total Amount */}
                <Box sx={{ 
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 2,
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <AssessmentIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                            {`${t('taxReports.yourTaxableIncome', 'Deine steuerpflichtigen Einkünfte')} ${selectedYear}`}
                        </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {isLoading ? (
                            <Skeleton width="100px" height="24px" />
                        ) : isPaid ? (
                            <Typography variant="h6" color="primary.main">
                                {formatAmount(totalAmount)}
                            </Typography>
                        ) : (
                            <Chip
                                icon={<LockOpenIcon sx={{ fontSize: 20 }} />}
                                label={t('taxReports.unlock', 'Freischalten')}
                                onClick={handleUnlockClick}
                                size="medium"
                                color="primary"
                                sx={{
                                    cursor: 'pointer',
                                    fontSize: '1rem',
                                    height: '40px',
                                    px: 3,
                                }}
                            />
                        )}
                    </Box>
                </Box>
            </Box>

            {/* Breakdown Section */}
            <Box sx={{ p: 2 }}>
                <Stack spacing={2}>
                    {displayDetails.map((detail, index) => (
                        <Box key={index} sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            py: 1,
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                {getCategoryIcon(index)}
                                <Typography variant="body1">
                                    {t(detail.translationKey, detail.label)}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                {isLoading ? (
                                    <Skeleton width="100px" height="24px" />
                                ) : isPaid ? (
                                    <Typography variant="body1" color="text.primary">
                                        {formatAmount(detail.amount)}
                                    </Typography>
                                ) : (
                                    <LockIcon sx={{ 
                                        color: 'text.secondary', 
                                        fontSize: 20,
                                        display: 'flex'
                                    }} />
                                )}
                            </Box>
                        </Box>
                    ))}
                </Stack>
            </Box>

            {/* Issues notification - clean and modern at the bottom */}
            {issueCount > 0 && (
                <Box 
                    sx={{ 
                        mt: 3,
                        p: 2,
                        bgcolor: (theme) => theme.palette.mode === 'dark' 
                            ? 'rgba(255, 193, 7, 0.08)'
                            : 'rgba(255, 193, 7, 0.08)',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                    onClick={onGoToSmartCheck}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <NotificationsOutlinedIcon sx={{ color: 'warning.main', fontSize: 20 }} />
                        <Typography variant="body1">
                            {(() => {
                                // Scenario 1: Only cost basis issues - Always show actual amount
                                if (costBasisCount > 0 && missingPriceCount === 0) {
                                    return t('taxReports.costBasisIssuesOnly', {
                                        count: costBasisCount,
                                        amount: formatCurrency(savingsPotential)
                                    });
                                }
                                
                                // Scenario 2: Only missing price issues - No amount (text only)
                                if (costBasisCount === 0 && missingPriceCount > 0) {
                                    return t('taxReports.missingPriceIssuesOnly', {
                                        count: missingPriceCount
                                    });
                                }
                                
                                // Scenario 3: Mixed issues - Show actual amount from cost basis portion
                                if (costBasisCount > 0 && missingPriceCount > 0) {
                                    return t('taxReports.mixedIssues', {
                                        count: issueCount,
                                        amount: formatCurrency(savingsPotential)
                                    });
                                }
                                
                                // Fallback
                                return `${issueCount} Hinweise erkannt – behebe sie und optimiere deine Steuern`;
                            })()}
                        </Typography>
                    </Box>
                    <ArrowForwardIcon sx={{ color: 'warning.main', fontSize: 20 }} />
                </Box>
            )}

        </Card>
    );
};

export default UnifiedTaxSummaryBox;